import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import { useBookingViewOnly } from "@/components/pages/Client/booking/BookingViewOnlyContext";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGetClientBookingQuoteQuery } from "@/redux/apis/clientBooking/clientBookingApi";
import { useAppSelector } from "@/redux/hooks";
import { buildBookingDiff, hasPricingChange } from "@/redux/slices/clientBooking/bookingDiff";
import { buildBookingPayload, getMissingBookingFields } from "@/redux/slices/clientBooking/bookingPayload";
import { selectBookingScope, selectClientBookingDraft, selectEditJobId, selectEditOriginal } from "@/redux/slices/clientBooking/selectors";

/**
 * Live quote for the current booking draft. Insulates the summary UI from
 * payload completeness: while the draft can't produce a valid payload the
 * query is skipped entirely (isQuotable=false) and `missingFields` says what's
 * blocking — the Price Summary degrades gracefully instead of firing doomed
 * requests. Re-quotes automatically whenever the draft changes.
 */
export const useBookingQuote = () => {
  const draft = useAppSelector(selectClientBookingDraft);
  const { accountId } = useClientScope();
  // Scope binding runs in ClientBooking's mount effect, which fires AFTER descendants render.
  // Until the draft is bound to this scope, the payload is still the previous scope's draft —
  // quoting it against the new account_uuid would cross accounts. Gate on the draft's own scope
  // matching the current one so no stale-payload quote goes out before initBookingScope resets.
  const scopeBound = useAppSelector(selectBookingScope) === (accountId ?? null);

  const payload = useMemo(() => buildBookingPayload(draft), [draft]);
  const missingFields = useMemo(() => getMissingBookingFields(draft), [draft]);

  // View-only (a closed booking opened via /client/booking/:jobId): show the stored price snapshot and
  // NEVER fire a live re-quote — the trip is finished, so today's price is wrong, and a no-cost booking
  // has no stored quote at all (don't fall back to quoting or to a "complete X to see pricing" prompt).
  const viewOnly = useBookingViewOnly();
  const storedQuote = draft.storedQuote;

  const editJobId = useAppSelector(selectEditJobId);
  const editOriginal = useAppSelector(selectEditOriginal);
  const isEdit = editJobId != null;

  /** Has the edit touched anything that changes the price? */
  const pricingChanged = useMemo(
    () => isEdit && !!editOriginal && hasPricingChange(buildBookingDiff(editOriginal, draft)),
    [isEdit, editOriginal, draft]
  );

  /**
   * Show the price the booking was actually invoiced at until the edit changes it.
   *
   * Opening a booking used to fire a live quote before the user touched anything, which was wrong
   * twice over. It spent a Google Directions call each time — the route cache isn't trusted until
   * it's been fetched three times, and resets whenever Google returns a different distance — and the
   * fresh quote could disagree with what the customer was charged, since tariffs and surcharges move.
   * The legacy sheet had it right: render the stored invoice, recalculate only on a real change.
   *
   * A booking with no stored price (no-cost, or pricing hidden from this client) still quotes live —
   * there's nothing to show otherwise.
   */
  const useStored = !!storedQuote && (viewOnly || !isEdit || !pricingChanged);
  const skipLive = useStored || viewOnly || !payload || !scopeBound;
  const { data: liveQuote, isFetching } = useGetClientBookingQuoteQuery(skipLive ? skipToken : { payload, accountId });

  return {
    /** Undefined until a quotable draft has been priced (or the stored price, where that stands). */
    quote: useStored ? storedQuote : viewOnly ? undefined : liveQuote,
    /** True when the draft is complete enough to quote (payload builds) and bound to this scope. */
    isQuotable: !!storedQuote || (!viewOnly && payload !== null && scopeBound),
    isLoading: useStored || viewOnly ? false : isFetching,
    /** Human-readable labels of what still blocks quoting/booking (none in view-only). */
    missingFields: useStored || viewOnly ? [] : missingFields,
  };
};
